// Escrituras de dinero en duda. La llave de idempotencia se guarda en este navegador ANTES de enviar,
// para reintentar con ella tras recargar o desde otra pestaña. En el navegador va solo la llave, la
// hora y el monto: el motivo nunca. El cuerpo completo vive en la memoria de la pestaña que lo envió.
// No se borran al cerrar sesión: protegen al recurso, no al usuario.
import { useState, useSyncExternalStore } from 'react';
import { forgetAttempt } from './idempotentAttempt';

/** Lo que se guarda en el navegador; quién, qué cadena y qué corte van en el id. */
interface StoredDoubt {
    v: 1;
    key: string;
    at: number;
    /** `null` si el barrido no pudo leerlo: obliga a capturar de nuevo. */
    amountCents: number | null;
    inFlight: boolean;
}

export interface WriteDoubtRecord {
    key: string;
    /** Cuándo se usó la llave por primera vez (ms). */
    at: number;
    amountCents: number;
}

export interface WriteDoubt<B> {
    key: string;
    /** Cuándo se usó la llave por primera vez; `null` si lo guardado no lo dice. */
    at: number | null;
    /** El monto enviado; `null` si lo guardado no sirve. */
    amountCents: number | null;
    /** Lo enviado completo: solo en la pestaña que lo envió. */
    body: B | null;
    /** La petición sigue en vuelo en esta pestaña. */
    sendingHere: boolean;
    /** Quedó guardada en vuelo y no la mandó esta pestaña. */
    inFlightElsewhere: boolean;
}

export type WriteDoubtState<B> =
    | { kind: 'none' }
    | { kind: 'doubt'; doubt: WriteDoubt<B> }
    /** Pasó el plazo de la llave: ya no se puede confirmar ese intento. */
    | { kind: 'expired' };

/** La llave del motor vale de 7 a 14 días; pasados 6 ya no se confía en ella. */
export const DOUBT_TTL_MS = 6 * 24 * 60 * 60 * 1000;

const PREFIX = 'fiado:write-doubt:';

/** Lo guardado (o borrado, con `null`) cuando el navegador no dejó hacerlo. */
const fallback = new Map<string, string | null>();

/** Un cuerpo completo vale solo para la llave y el monto con que se envió. */
interface KeptBody {
    key: string;
    amountCents: number;
    body: unknown;
}

/** Los cuerpos completos, con el motivo: solo en memoria. */
const bodies = new Map<string, KeptBody>();
/** Escrituras en vuelo desde esta pestaña. */
const sending = new Set<string>();
/** Las escrituras que pasaron por aquí en esta sesión. */
const known = new Set<string>();
const listeners = new Set<() => void>();
let version = 0;

export function writeDoubtStorageKey(id: string): string {
    return `${PREFIX}${id}`;
}

/** Solo llave, hora y monto de escrituras de dinero: sin tokens ni datos personales. */
function browserStorage(): Storage | null {
    try {
        // eslint-disable-next-line no-restricted-syntax -- llaves de idempotencia, sin tokens ni PII
        return window.localStorage;
    } catch {
        return null;
    }
}

function notify() {
    version += 1;
    for (const listener of listeners) listener();
}

function loadRaw(id: string): string | null {
    if (fallback.has(id)) return fallback.get(id) ?? null;
    try {
        return browserStorage()?.getItem(writeDoubtStorageKey(id)) ?? null;
    } catch {
        return null;
    }
}

function storeRaw(id: string, raw: string | null) {
    try {
        const storage = browserStorage();
        if (storage === null) throw new Error('Sin almacenamiento');
        if (raw === null) storage.removeItem(writeDoubtStorageKey(id));
        else storage.setItem(writeDoubtStorageKey(id), raw);
        fallback.delete(id);
    } catch {
        // Sin almacenamiento la duda sigue en memoria; un borrado que falló deja su marca para no
        // volver a leer el valor viejo.
        fallback.set(id, raw);
    }
}

function preventLeaving(event: BeforeUnloadEvent) {
    event.preventDefault();
    // Algunos navegadores todavía piden el valor para mostrar la confirmación.
    // eslint-disable-next-line @typescript-eslint/no-deprecated -- compatibilidad con navegadores viejos
    event.returnValue = '';
}

/** Con un envío en vuelo, salir de la página pide confirmación. */
function setSending(id: string, on: boolean) {
    const wasSending = sending.size > 0;
    if (on) sending.add(id);
    else sending.delete(id);
    if (!wasSending && sending.size > 0) window.addEventListener('beforeunload', preventLeaving);
    if (wasSending && sending.size === 0)
        window.removeEventListener('beforeunload', preventLeaving);
}

interface ParsedDoubt {
    key: string;
    at: number | null;
    amountCents: number | null;
    inFlight: boolean;
}

/** Lo guardado, validado por tipo. Sin llave no hay nada que conservar. */
function parse(raw: string | null): ParsedDoubt | null {
    if (raw === null) return null;
    let value: unknown;
    try {
        value = JSON.parse(raw);
    } catch {
        return null;
    }
    if (typeof value !== 'object' || value === null) return null;
    const { v, key, at, amountCents, inFlight } = value as Record<string, unknown>;
    if (typeof key !== 'string' || key === '') return null;
    const atMs = typeof at === 'number' && Number.isFinite(at) ? at : null;
    // El ajuste puede ser negativo; ninguno vale 0.
    const amount =
        typeof amountCents === 'number' && Number.isInteger(amountCents) && amountCents !== 0
            ? amountCents
            : null;
    const complete = v === 1 && atMs !== null && amount !== null;
    // Si lo demás no sirve, se conserva la llave y la pantalla pide capturar de nuevo.
    return { key, at: atMs, amountCents: complete ? amount : null, inFlight: inFlight === true };
}

const isExpired = (at: number | null, now: number) => at !== null && now - at > DOUBT_TTL_MS;

/** El cuerpo en memoria, solo si es de la llave y el monto guardados. */
function keptBodyFor(id: string, parsed: ParsedDoubt): unknown {
    const kept = bodies.get(id);
    if (kept === undefined || parsed.amountCents === null) return null;
    return kept.key === parsed.key && kept.amountCents === parsed.amountCents ? kept.body : null;
}

/** Suelta los cuerpos cuya llave ya no es la guardada (otra pestaña resolvió o la reemplazó). */
function pruneBodies(id?: string) {
    for (const [kept, entry] of bodies) {
        if (id !== undefined && kept !== id) continue;
        if (parse(loadRaw(kept))?.key !== entry.key) bodies.delete(kept);
    }
}

function stateOf<B>(id: string, raw: string | null, now: number): WriteDoubtState<B> {
    const parsed = parse(raw);
    if (parsed === null) return { kind: 'none' };
    if (isExpired(parsed.at, now)) return { kind: 'expired' };
    const sendingHere = sending.has(id);
    const { inFlight, ...doubt } = parsed;
    return {
        kind: 'doubt',
        doubt: {
            ...doubt,
            // Lo guardó este módulo con el cuerpo de esa escritura.
            body: keptBodyFor(id, parsed) as B | null,
            sendingHere,
            inFlightElsewhere: inFlight && !sendingHere,
        },
    };
}

export function readWriteDoubt<B>(id: string, now = Date.now()): WriteDoubtState<B> {
    return stateOf<B>(id, loadRaw(id), now);
}

function save(id: string, record: WriteDoubtRecord, body: unknown, inFlight: boolean): string {
    const stored: StoredDoubt = { v: 1, ...record, inFlight };
    const raw = JSON.stringify(stored);
    known.add(id);
    storeRaw(id, raw);
    if (body === null) bodies.delete(id);
    else bodies.set(id, { key: record.key, amountCents: record.amountCents, body });
    return raw;
}

/**
 * Justo antes de enviar: si la pestaña se cierra o se recarga, la llave ya está guardada. Devuelve
 * lo escrito, para saber después si otra pestaña lo cambió.
 */
export function beginWrite(id: string, record: WriteDoubtRecord, body: unknown): string {
    const raw = save(id, record, body, true);
    setSending(id, true);
    notify();
    return raw;
}

/** Un envío que falló sin respuesta clara: pudo quedar escrito. */
export function saveWriteDoubt(id: string, record: WriteDoubtRecord, body: unknown): void {
    save(id, record, body, false);
    setSending(id, false);
    notify();
}

export function clearWriteDoubt(id: string): void {
    storeRaw(id, null);
    bodies.delete(id);
    setSending(id, false);
    notify();
}

export interface WriteSnapshot {
    raw: string | null;
    body: KeptBody | undefined;
}

/** Como estaba la duda antes de un envío. */
export function takeWriteSnapshot(id: string): WriteSnapshot {
    return { raw: loadRaw(id), body: bodies.get(id) };
}

/**
 * Un envío que no escribió deja la duda como estaba, salvo que otra pestaña la haya cambiado desde
 * `beginWrite` (`written`): entonces manda lo de esa pestaña.
 */
export function restoreWriteSnapshot(id: string, snapshot: WriteSnapshot, written: string): void {
    if (loadRaw(id) === written) {
        storeRaw(id, snapshot.raw);
        if (snapshot.body === undefined) bodies.delete(id);
        else bodies.set(id, snapshot.body);
    } else pruneBodies(id);
    setSending(id, false);
    notify();
}

/** Borra del navegador las dudas caducadas o ilegibles. Nunca lanza. */
export function sweepWriteDoubts(now = Date.now()): void {
    try {
        const storage = browserStorage();
        if (storage === null) return;
        const ids: string[] = [];
        for (let i = 0; i < storage.length; i += 1) {
            const name = storage.key(i);
            if (name?.startsWith(PREFIX)) ids.push(name.slice(PREFIX.length));
        }
        for (const id of ids) {
            const name = writeDoubtStorageKey(id);
            const parsed = parse(storage.getItem(name));
            if (parsed === null || isExpired(parsed.at, now)) storage.removeItem(name);
            else if (parsed.at === null) {
                // Sin hora: arranca el plazo y se quitan los campos ajenos.
                const fixed: StoredDoubt = {
                    v: 1,
                    key: parsed.key,
                    at: now,
                    amountCents: null,
                    inFlight: false,
                };
                storage.setItem(name, JSON.stringify(fixed));
            }
        }
    } catch {
        // Un navegador que no deja leer no impide trabajar.
    }
}

/** Al cerrar sesión: olvida las llaves y los cuerpos en memoria; lo guardado se queda. */
export function forgetMoneyWritesInMemory(): void {
    for (const id of known) forgetAttempt(id);
    known.clear();
    bodies.clear();
    notify();
}

// Cuando otra pestaña resuelve o reemplaza una duda, esta suelta los cuerpos de esa llave, aunque
// ningún diálogo esté abierto.
window.addEventListener('storage', (event) => {
    if (event.key === null) pruneBodies();
    else if (event.key.startsWith(PREFIX)) pruneBodies(event.key.slice(PREFIX.length));
});

function subscribe(listener: () => void): () => void {
    listeners.add(listener);
    // Otra pestaña guardó o borró una duda.
    const onStorage = (event: StorageEvent) => {
        if (event.key === null || event.key.startsWith(PREFIX)) {
            version += 1;
            listener();
        }
    };
    window.addEventListener('storage', onStorage);
    return () => {
        listeners.delete(listener);
        window.removeEventListener('storage', onStorage);
    };
}

/** La duda de una escritura, al día con esta pestaña y con las demás. */
export function useWriteDoubt<B>(id: string): WriteDoubtState<B> {
    // Cualquier cambio, de esta pestaña o de otra, vuelve a pintar.
    useSyncExternalStore(subscribe, () => version);
    // El plazo se mide desde que se abrió la pantalla.
    const [openedAt] = useState(() => Date.now());
    return readWriteDoubt<B>(id, openedAt);
}

/** Solo para pruebas: simula una recarga del navegador. */
export function resetWriteDoubtMemory(): void {
    fallback.clear();
    bodies.clear();
    known.clear();
    for (const id of [...sending]) setSending(id, false);
    version += 1;
}
